import type { ExpandedState, OnChangeFn } from '@tanstack/react-table';

/** Что известно таблице о раскрытии и пагинации на момент сборки опций. */
export interface ExpandingInput<T> {
  /** Дерево строк: функция-извлекатель детей (undefined — плоская таблица) */
  getSubRows?: (originalRow: T, index: number) => T[] | undefined;
  /** Пагинация серверная: нарезку делает источник данных, не таблица */
  isManualPagination: boolean;
  /** Раскрытием управляет потребитель (undefined — владеет TanStack) */
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
}

/** Опции и срез состояния TanStack, относящиеся к раскрытию строк. */
export interface ExpandingConfig {
  options: {
    paginateExpandedRows: boolean;
    onExpandedChange?: OnChangeFn<ExpandedState>;
  };
  state: { expanded?: ExpandedState };
}

/**
 * Собирает опции раскрытия для TanStack.
 *
 * Вынесено из хука отдельной чистой функцией не для красоты: в этих трёх
 * строчках дважды случались регрессии, и тестировать их надо без React.
 *
 * Правила:
 *
 * 1. `paginateExpandedRows: false` — только для дерева с КЛИЕНТСКОЙ пагинацией:
 *    там страница считается по корневым строкам, а дети выводятся сверх неё
 *    (иначе pageSize «съедали» бы дети).
 *    При серверной пагинации флаг обязан остаться `true`: с `manualPagination`
 *    TanStack не строит пагинированную модель (RowPagination: возвращает
 *    pre-pagination), а `getExpandedRowModel` при `false` намеренно НЕ
 *    раскрывает строки, полагаясь на пагинацию — дети не появятся вообще.
 *    Страницу в этом режиме и так нарезает источник данных.
 *
 * 2. `onExpandedChange` и `expanded` попадают в конфиг ТОЛЬКО когда раскрытием
 *    управляет потребитель. Ключ со значением `undefined` перебил бы дефолт
 *    TanStack (`makeStateUpdater('expanded')`), потому что опции мержатся
 *    спредом — и `setExpanded` стал бы пустышкой, убив раскрытие во всех
 *    таблицах, включая блок под строкой (`renderSubComponent`).
 */
export function buildExpandingConfig<T>({
  getSubRows,
  isManualPagination,
  expanded,
  onExpandedChange,
}: ExpandingInput<T>): ExpandingConfig {
  return {
    options: {
      paginateExpandedRows: !(getSubRows && !isManualPagination),
      ...(onExpandedChange ? { onExpandedChange } : {}),
    },
    state: expanded === undefined ? {} : { expanded },
  };
}
